import React from 'react';
import { Autocomplete, TextField, Paper } from '@mui/material';
import Keyword from 'data/Keyword';
import { BorderRadius } from 'styles/themeBorderRadius';
import { Background, Text } from 'styles/themeColors';
import { Space } from 'styles/themeSpaces';
import { Height } from 'styles/themeHeights';

interface AutosuggestRenderInputProps {
  addTag?: (name: string) => void;
  value?: string;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  keywords?: Keyword[];
  placeholder?: string;
  ref?: React.Ref<HTMLInputElement>;
  [key: string]: any;
}

const AutosuggestRenderInput = ({ addTag, keywords = [], value, onChange, placeholder, ...props }: AutosuggestRenderInputProps) => {
  const inputValue = (value && value.trim().toLowerCase()) || '';
  const inputLength = inputValue.length;

  const filteredSuggestions = keywords.filter((keyword: Keyword) => {
    return keyword.name.toLowerCase().slice(0, inputLength) === inputValue;
  });

  return (
    <Autocomplete
      freeSolo
      disableClearable
      options={filteredSuggestions}
      slotProps={{
        popper: {
          style: { width: 'auto', minWidth: 250 }
        }
      }}
      getOptionLabel={(option) => typeof option === 'string' ? option : option.name}
      inputValue={value || ''}
      onInputChange={(_, newValue, reason) => {
        if (reason !== 'reset' && onChange) {
          const syntheticEvent = {
            target: { value: newValue }
          } as React.ChangeEvent<HTMLInputElement>;
          onChange(syntheticEvent);
        }
      }}
      onChange={(_, selectedValue) => {
        if (addTag && selectedValue && typeof selectedValue !== 'string') {
          addTag(selectedValue.name);
        }
      }}
      PaperComponent={(paperProps) => (
        <Paper {...paperProps} sx={{
          mt: '2px',
          '& .MuiAutocomplete-listbox': {
            padding: 0,
          },
          '& .MuiAutocomplete-option': {
            color: Text.primary,
            backgroundColor: Background.white,
            display: 'flex',
            lineHeight: Height.xsPlus,
            fontSize: Space.sp2x,
            padding: '8px 16px',
            borderRadius: BorderRadius.s,
            cursor: 'pointer',
            '&[aria-selected="true"]': {
              backgroundColor: Background.shaded,
            },
            '&.Mui-focused': {
              backgroundColor: Background.shaded,
            },
          },
        }} />
      )}
      renderInput={(params) => (
        <TextField
          {...params}
          {...props}
          placeholder={placeholder}
          variant="standard"
          InputProps={{
            ...params.InputProps,
            disableUnderline: true,
          }}
          sx={{
            '& .MuiInputBase-root': {
              padding: 0,
              minWidth: '200px',
              width: '100%',
            },
            '& .MuiInputBase-input': {
              padding: '4px 0',
              minWidth: '200px',
              width: '100%',
            },
          }}
        />
      )}
      renderOption={(optionProps, option) => (
        <li {...optionProps}>
          <span>{option.name}</span>
        </li>
      )}
    />
  );
};

export default AutosuggestRenderInput;
